import * as React from "react";
import { sortBy } from "lodash";

export type ScoreCalculationNode = {
    match: boolean;
    value?: number;
    // the description is only used if we don't have a niceDescription
    description?: string;
    niceDescription?: React.ReactNode;
    details?: ScoreCalculationNode[];
};

/**
 * This is the main function for the score explanation analysis. It takes the explanation as given by Solr, applies
 * various simplifications and detects some parts that we handle specially.
 */
export function simplifyScoreExplanation(explanation: ScoreCalculationNode): ScoreCalculationNode {
    // Remove trivial "boost" factor
    if (
        isProduct(explanation) &&
        explanation.details &&
        explanation.details.length === 2 &&
        explanation.details[0].value === 1
    ) {
        return simplifyScoreExplanation(explanation.details[1]);
    }
    // Detect when we have a 'simple' description (which just says e.g. "product of:") and use that as the nice description
    if (explanation.description && explanation.description.match(/ of:$/)) {
        explanation = { ...explanation, niceDescription: explanation.description };
    }
    // Detect boost function and handle that specially
    if (
        isProduct(explanation) &&
        explanation.details &&
        explanation.details.length === 2 &&
        explanation.details[1] &&
        explanation.details[1].description &&
        explanation.details[1].description.match(/^product\(/)
    ) {
        const fieldDetails = explanation.details[0];
        const boostDetails = explanation.details[1];
        return {
            ...explanation,
            details: [simplifyScoreExplanation(fieldDetails), analyzeBoostExplanation(boostDetails)]
        };
    }
    // Remove trivial node
    if (
        explanation.details &&
        explanation.details.length === 1 &&
        explanation.description &&
        (explanation.description.match(/^(sum|product|max .*) of:$/) ||
            // Solr 8 introduced some huge descriptions starting with `weight` but they
            // are not a single field match explanation, so we can skip these as well.
            (explanation.description.match(/^weight\(/) && !explanation.description.includes("[SchemaSimilarity]")))
    ) {
        return simplifyScoreExplanation(explanation.details[0]);
    }
    // Analyze explanation of field match specially
    if (isFieldMatchExplanation(explanation)) {
        return simplifyFieldMatchExplanation(explanation);
    }
    // simplify recursively
    let simplifiedDetails =
        explanation.details && Array.isArray(explanation.details)
            ? explanation.details.map(simplifyScoreExplanation)
            : explanation.details;
    // Sort by score for max
    if (explanation.description && explanation.description.match(/^max /)) {
        simplifiedDetails = sortBy(simplifiedDetails, (node) => node.value && -node.value);
    } else if (explanation.description && explanation.description.match(/^product\(/)) {
        const boostDetails = analyzeBoostExplanation(explanation);
        return {
            ...boostDetails
        };
    }
    return {
        ...explanation,
        details: simplifiedDetails
    };
}

/**
 * This analyzes the explanation nodes for field matches; we parse the description to get the field name and the words
 * that this part of the query was looking for.
 */
function simplifyFieldMatchExplanation(explanation: ScoreCalculationNode): ScoreCalculationNode {
    const result = {
        ...explanation,
        details: undefined,
        extendedDetails: explanation.details
    };
    const fields = explanation.description && parseFieldWeight(new StringCursor(explanation.description));
    if (!fields) {
        return result;
    }
    const fieldNames = fields.map((f) => f[0]);
    const terms = fields.map((f) => f[1]);
    // We expect all field names to be the same
    if (fieldNames.length === 0 || !fieldNames.every((f) => f === fieldNames[0])) {
        return result;
    }
    let boost = null;
    let idf = null;
    // It's `tfNorm` for Solr 7 but just `tf` for Solr 8.
    let tfNorm = null;
    let tf = null;
    if (explanation.details && explanation.details[0] && explanation.details[0].details) {
        const inner = explanation.details[0].details;
        boost = getDetailValue(inner, /^boost/);
        idf = getDetailValue(inner, /^idf/);
        tfNorm = getDetailValue(inner, /^tfNorm,/);
        tf = getDetailValue(inner, /^tf,/);
    }
    const fieldName = fieldNames[0];
    const explainedFieldName = fieldName && explainFieldName(fieldName);
    result.niceDescription = (
        <React.Fragment>
            Field <span style={{ fontFamily: "monospace" }}>{fieldName}</span>{" "}
            {explainedFieldName && (
                <span>
                    (<b>{explainedFieldName}</b>)
                </span>
            )}{" "}
            matching {terms.length > 1 && "one of "}
            {terms.map((t, i) => [i > 0 ? ", " : null, <b>{t}</b>])}
            {boost !== null && (
                <React.Fragment>
                    <br />
                    boost {String(boost)}
                </React.Fragment>
            )}
            {idf !== null && tfNorm !== null && (
                <React.Fragment>
                    ; idf={String(idf)}, tfNorm={String(tfNorm)}
                </React.Fragment>
            )}
            {idf !== null && tf !== null && (
                <React.Fragment>
                    ; idf={String(idf)}, tf={String(tf)}
                </React.Fragment>
            )}
            <span />
        </React.Fragment>
    );
    return result;
}

function explainFieldName(fieldName: string): string | null {
    const withoutLangSuffix = fieldName.replace(/_\w\w$/, "");
    const withoutPrefixSuffix = withoutLangSuffix.replace(/_prefix$/, "");
    const explanations = {
        description: "article title",
        descriptionImportant: "beginning of article title",
        category_ids: "exact category match",
        categories: "category title",
        brand_txt: "brand",
        subsysNumbers: "subsys number",
        electronicShelfLabel: "electronic shelf label",
        mgbArticleId: "article MGB number",
        mgbVariantId: "variant MGB number",
        mgbBundleId: "bundle MGB number",
        bettyArticleId: "article BTY ID",
        bettyArticleVariantId: "variant BTY ID",
        bettyBundleId: "bundle BTY ID",
        GTINs: "GTIN",
        offerIds: "marketplace offer ID"
    } as { [field: string]: string };
    if (withoutLangSuffix in explanations) {
        return explanations[withoutLangSuffix];
    } else if (withoutPrefixSuffix in explanations) {
        return "prefix of " + explanations[withoutPrefixSuffix];
    } else {
        return null;
    }
}

function isProduct(explanationNode: ScoreCalculationNode): boolean {
    return !!explanationNode.description && explanationNode.description === "product of:";
}

function isFieldMatchExplanation(explanation: ScoreCalculationNode): boolean {
    return !!explanation.description && !!explanation.description.match(/^weight\(/);
}

function getDetailValue(details: ScoreCalculationNode[], descriptionRegex: RegExp): number | null {
    const found = details.find((n) => n.description && n.description.match(descriptionRegex));
    return found && typeof found.value === "number" ? found.value : null;
}

/**
 * This analyzes the node of the explanation that contains all the boosting functions. These are in one big string, so
 * we have to (somewhat) parse that to tease it apart.
 */
function analyzeBoostExplanation(explanation: ScoreCalculationNode): ScoreCalculationNode {
    if (!explanation || typeof explanation.description !== "string") {
        return explanation;
    }
    const result = parseBoostFunctionDescription(new StringCursor(explanation.description));
    if (result) {
        return {
            ...explanation,
            niceDescription: "boost functions, product of:",
            description: "product of:",
            details: result
        };
    }
    return result || explanation;
}

// exported for testing
/**
 * This is a helper for parsing strings. It allows us to consume the string piece by piece.
 */
export class StringCursor {
    s: string;
    constructor(s: string) {
        this.s = s;
    }

    atEnd(): boolean {
        return this.s.length === 0;
    }

    // Check whether the beginning of the string matches the given regex.
    at(r: RegExp): boolean {
        const match = this.s.match(r);
        return !!match && match.index === 0;
    }

    // Check whether the beginning of the string matches the given regex, and if so 'consumes' the matching part; the
    // matching part gets removed from the string and returned.
    eat(r: RegExp): string | undefined | null {
        const match = this.s.match(r);
        if (!match || match.index === undefined || match.index > 0) {
            return null;
        }
        const text = match[0];
        this.s = this.s.substr(text.length);
        return text;
    }
}

// BOOST FUNCTION PARSING --------------------------------------------------------------------------

// exported for testing
/**
 * This is the entry point for parsing the long boost function explanation.
 */
export function parseBoostFunctionDescription(s: StringCursor) {
    // At root, there's a product
    if (!s.eat(/product\(/)) {
        return null;
    }
    const factors = [];
    while (!s.at(/\)/)) {
        factors.push(parseBoostFunctionFactor(s));
        s.eat(/,/);
    }
    s.eat(/\)/);
    return factors;
}

function parseBoostFunctionFactor(s: StringCursor): ScoreCalculationNode {
    // This can be one of several boost factors. We kind of hackily detect them by various regexes, and try to get the
    // actual factor out.
    if (s.at(/query\(FunctionScoreQuery\(searchable_CUSTOMER/)) {
        const score = parseQueryFunctionFactor(s);
        if (score !== null) {
            return {
                value: score,
                niceDescription: `customer searchability boost`,
                match: score !== 1
            };
        }
    } else if (s.at(/query\(FunctionScoreQuery\(promotion/)) {
        const score = parseQueryFunctionFactor(s);
        const promotionState = score !== 1 ? "active" : "not active";
        if (score !== null) {
            return { value: score, niceDescription: `promotion boost (${promotionState})`, match: score !== 1 };
        }
    } else if (s.at(/if\(query\(availability_/)) {
        return parseAvailability(s);
    } else if (s.at(/if\(bool\(isLeadingArticle/)) {
        const result = parseIfExpr(s, parseBoolField, parseConst, parseConst);
        if (result) {
            const leadingArticleState = result.conditionValue ? "active" : "not active";
            return {
                value: result.actualValue,
                niceDescription: `leading article boost (${leadingArticleState})`,
                match: result.conditionValue
            };
        }
    } else if (s.at(/if\(bool\(ownBrand/)) {
        const result = parseIfExpr(s, parseBoolField, parseConst, parseConst);
        if (result) {
            const ownBrandState = result.conditionValue ? "active" : "not active";
            return {
                value: result.actualValue,
                niceDescription: `own brand boost (${ownBrandState})`,
                match: result.conditionValue
            };
        }
    } else if (s.at(/if\(long\(ropoRank/)) {
        // if(long(ropoRank)=35,sum(const(1.0),div(const(33.3),long(ropoRank)=35)),const(1.0))
        let result: ScoreCalculationNode | null = parseUnknownFunctionFactor(s);
        if (result.description) {
            result = parseRopoRankBoost(result.description);
        }
        if (result) {
            return result;
        }
    } else if (s.at(/query\(FunctionScoreQuery\(bettyArticleVariantId/)) {
        const score = parseQueryFunctionFactor(s);
        const promotionState = score !== 1 ? "active" : "not active";
        if (score !== null) {
            return {
                value: score,
                niceDescription: `favourite boost (${promotionState})`,
                match: score !== 1
            };
        }
    } else if (s.at(/if\(lt\(float\(topsellers/)) {
        let result: ScoreCalculationNode | null = parseUnknownFunctionFactor(s);
        if (result.description) {
            result = hackilyParseTopsellerBoost(result.description);
        }
        return {
            niceDescription: "topseller boost",
            match: true,
            ...result
        };
    } else if (s.at(/query\(FunctionScoreQuery\(buyable_CUSTOMER/)) {
        const score = parseQueryFunctionFactor(s);
        if (score !== null) {
            return {
                value: score,
                niceDescription: `customer buyability boost`,
                match: score !== 1
            };
        }
    } else if (s.at(/if\(termfreq\(customerTargetGroups/)) {
        let result: ScoreCalculationNode | null = parseUnknownFunctionFactor(s);
        if (result.description) {
            result = parseCustomerTargetGroupBoost(result.description);
        }
        if (result) {
            return result;
        }
    }
    return parseUnknownFunctionFactor(s);
}

// Parses customer target group boosting like
// if(termfreq(customerTargetGroups,8)=0,const(5.0),const(1.0))
function parseCustomerTargetGroupBoost(s: string) {
    const regex = /if\(termfreq\(customerTargetGroups,.+?\)=(\d+),const\((\d+\.\d+)\),const\((\d+\.\d+)\)\)$/;
    const match = s.match(regex);
    if (!match) {
        return null;
    }
    const termfreq = Number(match[1]);
    const valueThen = Number(match[2]);
    const valueElse = Number(match[3]);
    if (termfreq > 0) {
        return {
            match: true,
            value: valueThen,
            niceDescription: `customer target group boost (active)`
        };
    } else {
        return {
            match: false,
            value: valueElse,
            niceDescription: `customer target group boost (not active)`
        };
    }
}

function hackilyParseTopsellerBoost(s: string): ScoreCalculationNode | null {
    // this is terrible and will break if we change the formula in any way
    const RANK_REGEX = /float\(topsellers(2?)(?:_[A-Z]+)?{[^}]*}\)=(\d+(?:.\d+)?)/;
    const FACTOR_REGEX = /sum\(const\(1\),product\(sub\(const\((\d+(?:.\d+)?)\),const\(1\)\)/;
    const rankMatch = s.match(RANK_REGEX);
    const factorMatch = s.match(FACTOR_REGEX);
    if (!rankMatch || !factorMatch) {
        return null;
    }
    const topsellerVar = rankMatch[1];
    const rank = Number(rankMatch[2]);
    const factor = Number(factorMatch[1]);
    if (rank === 0) {
        return {
            match: false,
            niceDescription: `topseller${topsellerVar} boost (no rank)`,
            value: 1
        };
    } else {
        const value = 1 + (factor - 1) / Math.log10(10 + rank);
        return {
            match: true,
            niceDescription: `topseller${topsellerVar} boost (rank ${rank})`,
            value
        };
    }
}

// Parses ROPO rank boost values like
// if(long(ropoRank)=35,sum(const(1.0),div(const(33.3),long(ropoRank)=35)),const(1.0))
function parseRopoRankBoost(s: string) {
    const regex =
        /if\(long\(ropoRank\)=(\d+),sum\(const\(\d+\.\d+\),div\(const\((\d+\.\d+)\),long\(ropoRank\)=\d+\)\),const\((\d+\.\d+)\)\)/;
    const match = s.match(regex);
    if (!match) {
        return null;
    }
    const rank = Number(match[1]);
    if (rank !== undefined) {
        return {
            match: true,
            value: 1 + Number(match[2]) / rank,
            niceDescription: `ROPO topseller boost (rank ${rank})`
        };
    } else {
        return {
            match: false,
            value: Number(match[3]),
            niceDescription: `ROPO topseller boost (no rank)`
        };
    }
}

/*
 * This is a recursive function used to parse availability. It cannot only handle simple if/else-queries returned by solr,
 * but also nested if/else-conditions, which is the reason why this function is recursive.
 */
function parseAvailability(s: StringCursor): ScoreCalculationNode {
    if (s.at(/query/)) {
        // Base case
        const availabilityStatus = getAvailabilityStatus(s);
        const niceDescription =
            `availability is ${availabilityStatus}` || `unknown solr query term when parsing availability`;
        return {
            niceDescription: niceDescription,
            value: parseQueryFunctionFactor(s) || undefined,
            match: true
        };
    } else if (s.eat(/if\(/)) {
        // Recursive case
        // Determine whether the case we are dealing with is about a product being available, limited or unavailable
        const availabilityStatus = getAvailabilityStatus(s) || "unknown";

        // Now, we are going to parse the solr-query's condition, the then-clause, and else-clause
        const conditionValue = parseQueryFunctionFactor(s) !== 0;
        if (conditionValue !== null) {
            s.eat(/,/);
        }
        const parsedThenValue = parseConst(s);
        if (parsedThenValue !== null) {
            s.eat(/,/);
        }
        const parsedElseValue = parseAvailability(s);
        if (parsedElseValue !== null) {
            s.eat(/\)/);
        }

        let outerValue: number | undefined;
        if (conditionValue) {
            // The then-case applies
            outerValue = parsedThenValue || undefined;
        } else {
            // The else-case applies
            outerValue = parsedElseValue?.value || undefined;
        }
        const thenMatch = conditionValue;
        const elseMatch = !conditionValue && (parsedElseValue?.match || false) && outerValue !== 1;
        const outerMatch = thenMatch || elseMatch;
        const thenActive = thenMatch ? "active" : "not active";
        const elseActive = elseMatch ? "active" : "not active";
        const thenValue = parsedThenValue === null ? undefined : parsedThenValue;
        const elseValue = parsedElseValue?.value;
        const thenNiceDescription = `if availability is '${availabilityStatus}' (${thenActive})`;
        const elseNiceDescription = `else (${elseActive})`;

        let elseDetails;
        if (parsedElseValue) {
            // If elseValue has details, we want to show them. Otherwise, the elseValue stems from the base-case where
            // a query-function-factor has been analyzed, which will not have any children, but instead is supposed to
            // be shown itself in the details
            elseDetails = parsedElseValue.details ? parsedElseValue.details : [parsedElseValue];
        }

        return {
            match: outerMatch,
            value: outerValue,
            niceDescription: "availability boost:",
            details: [
                {
                    // Here we are returning based on a parsed number
                    match: thenMatch,
                    value: thenValue,
                    niceDescription: thenNiceDescription
                },
                {
                    // Here are returning the parts of a ScoreCalculationNode
                    match: elseMatch,
                    value: elseValue,
                    niceDescription: elseNiceDescription,
                    details: elseDetails
                }
            ]
        };
    } else {
        // Error handling case
        return {
            match: false,
            value: undefined,
            description: "Availability could not be parsed unfortunately."
        };
    }
}

/*
 * This returns whether the type or status of availability we are currently analyzing is 'available', 'limited' or 'unavailable'.
 */
function getAvailabilityStatus(s: StringCursor): string | undefined {
    if (s.at(/query\(availability_AVAILABLE/) || s.at(/query\(FunctionScoreQuery\(availability_AVAILABLE/)) {
        return "available";
    } else if (s.at(/query\(availability_LIMITED/) || s.at(/query\(FunctionScoreQuery\(availability_LIMITED/)) {
        return "limited";
    } else if (s.at(/query\(availability_UNAVAILABLE/) || s.at(/query\(FunctionScoreQuery\(availability_UNAVAILABLE/)) {
        return "unavailable";
    }
    return undefined;
}

/**
 * This parses and calculates the value of an `if` expression. We have to know what to expect as the condition, then
 * branch and else branch.
 */
function parseIfExpr(
    s: StringCursor,
    parseCondition: (x0: StringCursor) => boolean | null,
    parseThen: (x0: StringCursor) => number | null,
    parseElse: (x0: StringCursor) => number | null
) {
    s.eat(/if\(/);
    const conditionValue = parseCondition(s);
    if (conditionValue === null || !s.eat(/,/)) {
        return null;
    }
    const thenValue = parseThen(s);
    if (thenValue === null || !s.eat(/,/)) {
        return null;
    }
    const elseValue = parseElse(s);
    if (elseValue === null || !s.eat(/\)/)) {
        return null;
    }
    return {
        conditionValue,
        thenValue,
        elseValue,
        // Solr doesn't tell us what the actual value was, so we have to simulate the if
        actualValue: conditionValue ? thenValue : elseValue
    };
}

const NUMBER_REGEX = /-?\d+(\.\d+)(E[+-]\d+)?/;

/**
 * This parses e.g. `const(1.2)`
 */
function parseConst(s: StringCursor): number | null {
    s.eat(/,/);
    if (!s.eat(/const\(/)) {
        return null;
    }
    const value = s.eat(NUMBER_REGEX);
    s.eat(/\)/);
    return Number(value);
}

/*
 * This parses e.g. `bool(isLeadingArticle)=true`
 */
function parseBoolField(s: StringCursor): boolean | null {
    if (!s.eat(/bool\(\w+\)=/)) {
        return null;
    }
    if (s.eat(/true/)) {
        return true;
    } else if (s.eat(/false/)) {
        return false;
    } else {
        return null;
    }
}

/**
 * This parses a query function, like `query(somequery,1.0)=77.0`. Since in our case we know the query we expect in that
 * location, we just skip over that and get the value, which Solr helpfully provides.
 */
function parseQueryFunctionFactor(s: StringCursor): number | null {
    s.eat(/query/);
    // we don't care about the query
    parseBalancedParens(s);
    if (s.eat(/=/)) {
        // this is the actual score of the query
        const score = s.eat(NUMBER_REGEX);
        return Number(score);
    } else {
        return null;
    }
}

/**
 * This just skips over an unknown function until it finds a comma or closing parens (while skipping over balanced
 * parens). E.g. in `foo(bar,baz())blub,`, we skip until the last comma.
 */
function parseUnknownFunctionFactor(s: StringCursor): ScoreCalculationNode {
    // basically skip balanced parens until we get a comma or closing parens
    const stringBeforeSkipping = s.s;
    while (!s.at(/[,)]/) && !s.atEnd()) {
        s.eat(/[^(),]*/);
        if (s.at(/\(/)) {
            parseBalancedParens(s);
        }
    }
    // return everything that was skipped
    const description = stringBeforeSkipping.substr(0, stringBeforeSkipping.length - s.s.length);
    return { match: true, description };
}

/**
 * This 'parses', or rather skips over, balanced parentheses.
 */
function parseBalancedParens(s: StringCursor) {
    s.eat(/\(/);
    // count how many levels of parens we're in
    let levels = 1;
    // look for closing parens...
    while (levels > 0) {
        // skip to next opening or closing parens
        s.eat(/[^()]*/);
        if (s.eat(/\(/)) {
            levels += 1;
        } else if (s.eat(/\)/)) {
            // closing parens
            levels -= 1;
        } else {
            // end of string
            return null;
        }
    }
    return null;
}

// FIELD MATCH PARSING -----------------------------------------------------------------------------

// exported for testing
/**
 * This parses a field weight explanation like `weight(description_de:foo in 123)`.
 */
export function parseFieldWeight(
    s: StringCursor
): Array<[string | undefined | null, string | undefined | null]> | null {
    // "weight(...)"
    if (!s.eat(/weight\(/)) {
        return null;
    }
    const result = parseInnerFieldWeight(s);
    s.eat(/\)/);
    return result;
}

function atField(s: StringCursor): boolean {
    // "fieldName:..."
    return s.at(/\w+:/);
}

function parseField(s: StringCursor): [string | undefined | null, string | undefined | null] {
    // "fieldName:..."
    const name = s.eat(/\w+/);
    s.eat(/:/);
    let content;
    if (s.at(/\"/)) {
        s.eat(/\"/);
        content = s.eat(/[^"]*/);
        s.eat(/\"/);
    } else {
        content = s.eat(/[^ )]+/);
    }
    return [name, content];
}

/**
 * This parses a list of space-separated fields, e.g. `description_de:foo description_de:"bar baz"`
 */
function parseFields(s: StringCursor): Array<[string | undefined | null, string | undefined | null]> {
    const fields = [];
    while (atField(s)) {
        fields.push(parseField(s));
        s.eat(/\s+/);
    }
    return fields;
}

/**
 * Sometimes the field list is wrapped in `Synonym(...)`; this parses that.
 */
function parseSynonymExpr(s: StringCursor): Array<[string | undefined | null, string | undefined | null]> {
    s.eat(/Synonym\(/);
    const result = parseFields(s);
    s.eat(/\)/);
    return result;
}

/**
 * This parses the inner part of a field weight explanation like `description_de:foo in 123` or `Synonym(description_de:foo) in 123`.
 */
function parseInnerFieldWeight(s: StringCursor): Array<[string | undefined | null, string | undefined | null]> {
    let result;
    if (s.at(/Synonym\(/)) {
        // "Synonym(...)"
        result = parseSynonymExpr(s);
    } else {
        // field1:value1 field2:value2
        result = parseFields(s);
    }
    // the document id: " in 123"
    s.eat(/ in \d+/);
    return result;
}
